/**
 * Individual donor Overview, Location and Districts cards, laid out like the
 * voter detail page: a Location card (State, Place, Postal code, County, each
 * its entity page plus the filtered donors list), linked cycles and a
 * ZIP-derived Districts card. Pure HTML builders (no DOM), unit-tested in
 * test/individual-profile.test.ts.
 *
 * Donors carry only a ZIP: county and congressional district come from the
 * ZIP's land-area overlaps (PostalCode-OVERLAPS->County/District), so a ZIP that
 * spans several is shown with every overlap, never as one inferred district.
 * State legislative districts and precincts have no donor source and no row.
 */

import {
  displayValue,
  districtPath,
  entityLink,
  escapeHtml,
  filterPath,
  formatDate,
  htmlList,
  link,
  locationValue,
  statePath,
  titleCase
} from './leaf-detail';
import { canonicalPath } from '@electinfo/common';

type Scalar = string | number | null;

interface Overlap<TNode> {
  node: TNode;
  properties: { overlap_land_area: Scalar };
}

export interface OverlapCounty {
  id: string;
  name: string | null;
  state: string | null;
}

export interface OverlapDistrict {
  id: string;
  name: string | null;
  state: string | null;
  district_number: string | null;
  district_slug: string | null;
}

export interface DonorPostalCode {
  id: string;
  zip_code: string | null;
  overlapsCountiesConnection?: { edges: Array<Overlap<OverlapCounty>> };
  overlapsDistrictsConnection?: { edges: Array<Overlap<OverlapDistrict>> };
}

export interface DonorPlace {
  id: string;
  name: string | null;
  name_normalized?: string | null;
  state: string | null;
}

export interface DonorProfile {
  id: string;
  name: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  employer: string | null;
  occupation: string | null;
  cycles: string | null;
  first_contribution: string | null;
  last_contribution: string | null;
  party_affinity_dem?: Scalar;
  party_affinity_rep?: Scalar;
  employedByEmployersConnection: { edges: Array<{ node: { id: string; name: string | null } }> };
  residesInPlacesConnection: { edges: Array<{ node: DonorPlace }> };
  residesInPostalCodesConnection: { edges: Array<{ node: DonorPostalCode }> };
}

const donorsPath = (state: string | null | undefined, key: string, value: string | null | undefined): string | null =>
  filterPath('individuals', state, key, value);

const donorLocation = (label: string | null | undefined, entityHref: string | null, filterHref: string | null): string =>
  locationValue(label, entityHref, filterHref, 'donors', 'donor-filter-link');

const entityHref = (entityType: string, id: string | null | undefined): string | null =>
  id ? `/${canonicalPath(entityType, id)}/` : null;

const area = (value: Scalar): number => Number(value) || 0;

function byArea<T>(edges: Array<Overlap<T>> | undefined): T[] {
  return [...(edges || [])].sort((a, b) => area(b.properties.overlap_land_area) - area(a.properties.overlap_land_area))
    .map(edge => edge.node);
}

/** The donor's own ZIP among its RESIDES_IN postal codes, else the first. */
export function donorPostalCode(donor: DonorProfile): DonorPostalCode | null {
  const zips = donor.residesInPostalCodesConnection.edges.map(edge => edge.node);
  const own = (donor.zip_code || '').trim().slice(0, 5);
  return zips.find(zip => own && (zip.zip_code || '').trim() === own) || zips[0] || null;
}

/** "2024, 2022, ..." as links to the donors who gave in that cycle, newest first. */
export function cycleLinksHtml(cycles: string | null | undefined): string {
  const years = [...new Set((cycles || '').split(/[^0-9]+/).filter(y => /^\d{4}$/.test(y)))]
    .sort((a, b) => Number(b) - Number(a));
  if (!years.length) return displayValue(null);
  return years.map(year => link(donorsPath(null, 'cycle', year), year)).join(', ');
}

/**
 * The donor's giving lean, in the individuals page's "Party lean (giving)"
 * bands (by Democratic share): "Lean Democratic (72% D, 28% R)". Never a party
 * registration.
 */
export function partyLeanLabel(dem: Scalar | undefined, rep: Scalar | undefined): string {
  if (dem === null || dem === undefined || dem === '') return displayValue(null);
  const d = Number(dem);
  if (!Number.isFinite(d)) return displayValue(null);
  const r = rep === null || rep === undefined || rep === '' || !Number.isFinite(Number(rep)) ? 1 - d : Number(rep);
  const band = d >= 0.8 ? 'Strong Democratic'
    : d >= 0.6 ? 'Lean Democratic'
    : d >= 0.4 ? 'Mixed'
    : d >= 0.2 ? 'Lean Republican'
    : 'Strong Republican';
  const pct = (x: number) => Math.round(Math.max(0, Math.min(1, x)) * 100);
  return escapeHtml(`${band} (${pct(d)}% D, ${pct(r)}% R)`);
}

/** The employer as its Employer page when the donor's EMPLOYED_BY edge names it, else the filtered donors list. */
function employerHtml(donor: DonorProfile): string {
  const employer = (donor.employer || '').trim();
  if (!employer) return displayValue(null);
  const key = employer.toUpperCase();
  const match = donor.employedByEmployersConnection.edges
    .find(({ node }) => (node.name || '').trim().toUpperCase() === key);
  return match ? entityLink('Employer', match.node.id, employer) : link(donorsPath(null, 'employer', employer), employer);
}

/** Largest-overlap county as its County page; a note when the ZIP spans more than one. */
export function countyHtml(zip: DonorPostalCode | null): string {
  const counties = byArea(zip?.overlapsCountiesConnection?.edges);
  if (!counties.length) return displayValue(null);
  const [first] = counties;
  const primary = entityLink('County', first.id, first.name || first.id);
  return counties.length > 1
    ? `${primary} <span class="meta">(ZIP ${escapeHtml(zip?.zip_code || '')} spans ${counties.length} counties)</span>`
    : primary;
}

export function overviewHtml(donor: DonorProfile): string {
  const occupation = donor.occupation ? link(donorsPath(null, 'occupation', donor.occupation), donor.occupation) : displayValue(null);

  return htmlList([
    ['Name', displayValue(donor.name)],
    ['Employer', employerHtml(donor)],
    ['Occupation', occupation],
    ['Party lean (giving)', partyLeanLabel(donor.party_affinity_dem, donor.party_affinity_rep)],
    ['Cycles', cycleLinksHtml(donor.cycles)],
    ['First contribution', formatDate(donor.first_contribution)],
    ['Last contribution', formatDate(donor.last_contribution)]
  ]);
}

/** The donor's RESIDES_IN place matching its city, else the first; null without one. */
export function donorPlace(donor: DonorProfile): DonorPlace | null {
  const places = donor.residesInPlacesConnection.edges.map(edge => edge.node);
  const city = (donor.city || '').trim().toUpperCase();
  return places.find(place => city && (place.name_normalized || '').trim().toUpperCase() === city) || places[0] || null;
}

/**
 * Location card: State, Place, Postal code and County, each as its entity page
 * plus the filtered donors list. Place and Postal code come from the donor's
 * RESIDES_IN edges; County from the ZIP's largest land overlap.
 */
export function locationHtml(donor: DonorProfile): string {
  const st = (donor.state || '').trim().toUpperCase() || null;
  const place = donorPlace(donor);
  const zip = donorPostalCode(donor);
  const zipCode = (donor.zip_code || '').trim().slice(0, 5) || zip?.zip_code || null;
  const city = donor.city ? titleCase(donor.city) : place?.name || null;
  return htmlList([
    ['State', donorLocation(st, statePath(st), donorsPath(st, 'state', st))],
    ['Place', [
      donorLocation(place?.name || city, place ? entityHref('Place', place.id) : null, donor.city ? donorsPath(st, 'city', donor.city) : null),
      // Rare (about 0.2% of donors): further RESIDES_IN places.
      ...donor.residesInPlacesConnection.edges.map(edge => edge.node).filter(other => other !== place)
        .map(other => entityLink('Place', other.id, other.name || other.id))
    ].join(', ')],
    ['Postal code', donorLocation(zipCode, zip ? entityHref('PostalCode', zip.id) : null, zipCode ? donorsPath(null, 'zip', zipCode) : null)],
    ['County', countyHtml(zip)]
  ]);
}

function districtLink(district: OverlapDistrict): string {
  const code = district.district_slug
    || (district.state && district.district_number ? `${district.state}-${district.district_number}` : null);
  const href = districtPath(code);
  return href ? link(href, code) : entityLink('District', district.id, code || district.name || district.id);
}

/**
 * Districts card: the ZIP's congressional district overlaps, largest land area
 * first. null (no card) when the ZIP has none.
 */
export function districtsHtml(donor: DonorProfile): string | null {
  const zip = donorPostalCode(donor);
  const districts = byArea(zip?.overlapsDistrictsConnection?.edges);
  if (!districts.length) return null;
  const links = districts.map(districtLink).join(', ');
  const note = districts.length > 1
    ? ` <span class="meta">(ZIP ${escapeHtml(zip?.zip_code || '')} overlaps ${districts.length} districts)</span>`
    : '';
  return htmlList([['Congressional district', links + note]])
    + '<p class="meta donor-districts-note">Districts are derived from the donor\'s ZIP code (land-area overlap), not a geocoded address.</p>';
}
